import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Input, Modal, Radio, Select } from 'antd';
import { transformToTitleCase } from '@utils/string';
import { Permission, PermissionAction, PermissionAsset } from '@redux/types/types';
import ModalTitle from '@modules/Roles/components/ModalTitle/ModalTitle';
import {
  CREATE_ROLE_DESCRIPTION,
  CREATE_ROLE_FORM_ITEMS,
  CUSTOM_TEXT,
  PERMISSION_WILDCARD,
  RoleType,
} from '@modules/Roles/constants/roles';
import PermissionsCollapseList from '@modules/Roles/features/PermissionsCollapseList/PermissionsCollapseList';
import useRoles from '@modules/Roles/hooks/useRoles';
import { RBACRole } from '@modules/Roles/types/roles';
import styles from './RoleEditorModal.module.scss';

interface Props {
  title: string;
  open: boolean;
  onOk: (payload: RBACRole) => void;
  onCancel: () => void;
  okText: string;
  role?: RBACRole;
}

const MODAL_WIDTH = 720;
const TEXT_AREA_ROWS = 4;

const DATA_TEST_ID = {
  MODAL: 'edit-role-modal',
  FORM: 'edit-role-form',
  ROLE_NAME: 'edit-role-form-role-name',
  ROLE_DESCRIPTION: 'edit-role-form-role-description',
  CREATE_ROLE_TYPE: 'edit-role-form-create-role-type',
  ROLE_TO_COPY: 'edit-role-form-role-to-copy',
  CREATE_ROLE_BUTTON: 'edit-role-form-create-role-button',
  ROLE_TO_COPY_SELECT: 'edit-role-form-role-to-copy-select',
};

const RoleEditorModal = ({ title, open, onOk, onCancel, okText, role }: Props) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState(RoleType.COPY_PRESET_ROLE);
  const [presetRolesOptions, setPresetRolesOptions] = useState<
    {
      label: string;
      value: string;
      description: string;
    }[]
  >([]);
  const [selectedPermissionsFromScratch, setSelectedPermissionsFromScratch] = useState<
    Permission[]
  >([]);
  const [selectedPermissionsFromExistingRole, setSelectedPermissionsFromExistingRole] = useState<
    Permission[]
  >([]);
  const [selectedRoleToCopy, setSelectedRoleToCopy] = useState<string>('');
  const presetRoleSelectRef = useRef<HTMLDivElement>(null);
  const [initialValues, setInitialValues] = useState({
    roleName: role?.role_name || '',
    roleDescription: role?.description || '',
    permissions: role?.permissions || [],
    roleType: !role ? RoleType.COPY_PRESET_ROLE : undefined,
  });

  const { roles, permissions } = useRoles();

  const [form] = Form.useForm();

  const validateForm = useCallback(
    (allFields: any) => {
      const { roleName, roleDescription, roleType, roleToCopy } = allFields;
      setSelectedRoleType(roleType);
      setSelectedRoleToCopy(roleToCopy || '');

      const isNameValid = roleName && roleName.trim().length > 0;
      const isDescriptionValid = roleDescription && roleDescription.trim().length > 0;

      if (roleType === RoleType.COPY_PRESET_ROLE) {
        const isRoleToCopyValid =
          roleToCopy && presetRolesOptions.some((r) => r.value === roleToCopy);
        setIsFormValid(isNameValid && isDescriptionValid && isRoleToCopyValid);
      } else if (roleType === RoleType.START_FROM_SCRATCH) {
        const hasSelectedPermissions = selectedPermissionsFromScratch.length > 0;
        setIsFormValid(isNameValid && isDescriptionValid && hasSelectedPermissions);
      } else {
        // For edit mode, check if the name, description, or permissions have changed
        const isNameDifferent = roleName !== role?.role_name;
        const isDescriptionDifferent = roleDescription !== role?.description;
        const isPermissionsDifferent =
          selectedPermissionsFromExistingRole.length !== role?.permissions.length ||
          selectedPermissionsFromExistingRole.some(
            (p) => !role?.permissions.some((rp) => rp.action === p.action && rp.asset === p.asset),
          );
        setIsFormValid(isNameDifferent || isDescriptionDifferent || isPermissionsDifferent);
      }
    },
    [presetRolesOptions, selectedPermissionsFromScratch, selectedPermissionsFromExistingRole, role],
  );

  const getSelectedRolePermissions = useCallback(() => {
    if (selectedRoleToCopy) {
      const selectedRole = roles.find((r) => r.role_name === selectedRoleToCopy);
      return selectedRole?.permissions || [];
    }
    return [];
  }, [roles, selectedRoleToCopy]);

  const onValuesFormChange = (_: any, allFields: any) => {
    form.setFieldsValue(allFields);

    validateForm(allFields);
  };

  const handleOk = () => {
    const { roleName, roleDescription } = form.getFieldsValue();

    let permissionsToUse: Permission[];
    if (selectedRoleType === RoleType.START_FROM_SCRATCH) {
      permissionsToUse = selectedPermissionsFromScratch.map((p) => ({
        action: p.action as PermissionAction,
        asset: p.asset as PermissionAsset,
      }));
    } else {
      // Use the modified permissions from the UI, not the original role permissions
      permissionsToUse = selectedPermissionsFromExistingRole;
    }

    // Remove wildcard permissions if there are different actions and assets
    const nonWildcardPermissions = permissionsToUse.filter((p) => p.asset !== PERMISSION_WILDCARD);
    const wildcardPermissions = permissionsToUse.filter((p) => p.asset === PERMISSION_WILDCARD);

    let finalPermissions = nonWildcardPermissions;

    // Only keep wildcard permissions if all non-wildcard permissions have the same action
    if (wildcardPermissions.length > 0 && nonWildcardPermissions.length > 0) {
      const uniqueActions = new Set(nonWildcardPermissions.map((p) => p.action));
      if (uniqueActions.size === 1) {
        // All non-wildcard permissions have the same action, so we can use wildcard
        finalPermissions = wildcardPermissions;
      }
      // If there are different actions, we keep only the non-wildcard permissions
    } else if (wildcardPermissions.length > 0 && nonWildcardPermissions.length === 0) {
      // Only wildcard permissions exist, keep them
      finalPermissions = wildcardPermissions;
    }

    onOk({ role_name: roleName, description: roleDescription, permissions: finalPermissions });
  };

  const handleCancel = () => {
    setSelectedPermissionsFromScratch([]);
    setSelectedPermissionsFromExistingRole([]);
    setSelectedRoleToCopy('');
    form.resetFields();
    onCancel();
  };

  const handlePermissionsChange = (
    selectedPermissionsList: { action: string; asset: string }[],
  ) => {
    const formattedPermissions = selectedPermissionsList.map((p) => ({
      action: p.action as PermissionAction,
      asset: p.asset as PermissionAsset,
    }));

    if (selectedRoleType === RoleType.START_FROM_SCRATCH) {
      setSelectedPermissionsFromScratch(formattedPermissions);
    } else {
      setSelectedPermissionsFromExistingRole(formattedPermissions);
    }
  };

  const getInitialSelectedPermissions = useCallback(() => {
    if (role) {
      return role.permissions;
    }
    if (selectedRoleType === RoleType.COPY_PRESET_ROLE) {
      return getSelectedRolePermissions();
    }
    return selectedPermissionsFromScratch;
  }, [selectedRoleType, selectedPermissionsFromScratch, getSelectedRolePermissions, role]);

  useEffect(() => {
    if (open) setSelectedRoleType(RoleType.COPY_PRESET_ROLE);
  }, [open]);

  useEffect(() => {
    if (roles.length > 0) {
      setPresetRolesOptions(
        roles
          .filter((r) => !r.is_custom)
          .map((r) => ({
            label: transformToTitleCase(r.role_name),
            value: r.role_name,
            description: r.description,
          })),
      );
    }
  }, [roles]);

  useEffect(() => {
    if (open) validateForm(form.getFieldsValue());
  }, [
    selectedPermissionsFromScratch,
    selectedPermissionsFromExistingRole,
    form,
    validateForm,
    selectedRoleType,
    open,
  ]);

  useEffect(() => {
    if (role) {
      // Set form values for edit mode
      // Note: We need both form.setFieldsValue() and setInitialValues() because:
      // - form.setFieldsValue() updates the existing form's field values (works when form already exists)
      // - setInitialValues() sets the initial values for new form instances (works when form is recreated)
      form.setFieldsValue({
        roleName: role.role_name,
        roleDescription: role.description,
        roleType: undefined,
      });
      setInitialValues({
        roleName: role.role_name,
        roleDescription: role.description,
        permissions: role.permissions,
        roleType: undefined,
      });
      setSelectedPermissionsFromExistingRole(role.permissions);
    } else {
      // Reset form for create mode
      form.setFieldsValue({
        roleName: '',
        roleDescription: '',
        roleType: RoleType.COPY_PRESET_ROLE,
      });
      setInitialValues({
        roleName: '',
        roleDescription: '',
        permissions: [],
        roleType: RoleType.COPY_PRESET_ROLE,
      });
      // Reset permission selections
      setSelectedPermissionsFromScratch([]);
      setSelectedPermissionsFromExistingRole([]);
      setSelectedRoleToCopy('');
    }
  }, [role, form]);

  const renderPresetRolesForm = () => {
    return (
      <div className={styles.presetRoleSelect} ref={presetRoleSelectRef}>
        <Form.Item
          label={CREATE_ROLE_FORM_ITEMS.roleToCopy.label}
          name={CREATE_ROLE_FORM_ITEMS.roleToCopy.name}
          data-testid={DATA_TEST_ID.ROLE_TO_COPY}
        >
          <Select
            placeholder={CREATE_ROLE_FORM_ITEMS.roleToCopy.placeholder}
            options={presetRolesOptions}
            optionRender={(option) => {
              const roleData = presetRolesOptions.find((r) => r.value === option.value);
              return (
                <div className={styles.presetRoleItem}>
                  <h6>{option.label}</h6>
                  {roleData?.description && (
                    <p className={styles.presetRoleDescription}>{roleData.description}</p>
                  )}
                </div>
              );
            }}
            getPopupContainer={() => presetRoleSelectRef.current || document.body}
            data-testid={DATA_TEST_ID.ROLE_TO_COPY_SELECT}
            allowClear
          />
        </Form.Item>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <Form
        layout="vertical"
        className={styles.form}
        onValuesChange={onValuesFormChange}
        initialValues={initialValues}
        form={form}
        data-testid={DATA_TEST_ID.FORM}
      >
        <Form.Item
          label={CREATE_ROLE_FORM_ITEMS.roleName.label}
          name={CREATE_ROLE_FORM_ITEMS.roleName.name}
        >
          <Input
            placeholder={CREATE_ROLE_FORM_ITEMS.roleName.placeholder}
            data-testid={DATA_TEST_ID.ROLE_NAME}
          />
        </Form.Item>
        <Form.Item
          label={CREATE_ROLE_FORM_ITEMS.roleDescription.label}
          name={CREATE_ROLE_FORM_ITEMS.roleDescription.name}
        >
          <Input.TextArea
            placeholder={CREATE_ROLE_FORM_ITEMS.roleDescription.placeholder}
            rows={TEXT_AREA_ROWS}
            data-testid={DATA_TEST_ID.ROLE_DESCRIPTION}
          />
        </Form.Item>
        {!role && (
          <Form.Item
            label={CREATE_ROLE_FORM_ITEMS.createRoleType.label}
            name={CREATE_ROLE_FORM_ITEMS.createRoleType.name}
            data-testid={DATA_TEST_ID.CREATE_ROLE_TYPE}
          >
            <Radio.Group
              onChange={(e) => setSelectedRoleType(e.target.value as RoleType)}
              value={selectedRoleType}
            >
              {CREATE_ROLE_FORM_ITEMS.createRoleType.options.map((r) => (
                <Radio key={r.value} value={r.value}>
                  {r.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        )}
        {selectedRoleType === RoleType.COPY_PRESET_ROLE && renderPresetRolesForm()}
        {(selectedRoleType === RoleType.START_FROM_SCRATCH ||
          (selectedRoleType === RoleType.COPY_PRESET_ROLE && selectedRoleToCopy) ||
          role) && (
          <PermissionsCollapseList
            permissions={permissions}
            onPermissionsChange={handlePermissionsChange}
            initialSelectedPermissions={getInitialSelectedPermissions()}
          />
        )}
      </Form>
    );
  };

  return (
    <Modal
      title={<ModalTitle title={title} subtitle={CUSTOM_TEXT} onCancel={handleCancel} />}
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      okButtonProps={{
        disabled: !isFormValid,
        'data-testid': DATA_TEST_ID.CREATE_ROLE_BUTTON,
      }}
      okText={okText}
      centered
      width={MODAL_WIDTH}
      className={styles.modal}
      closable={false}
      data-testid={DATA_TEST_ID.MODAL}
    >
      {open && (
        <>
          <p className={styles.modal_description}>{CREATE_ROLE_DESCRIPTION}</p>
          {renderForm()}
        </>
      )}
    </Modal>
  );
};

export default RoleEditorModal;
